import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { AppMarketPageIdentifier, AppMarketRouteComponentProps } from '../../AppRoutes';
import { useFetchInstalledAppsQuery } from '../../redux/api/appMarket-api-slice';
import { App, AppSubscriptionCountry } from '../../models/app';
import { ListWithLoadMore } from '../../components/Common/ListWithLoadMore';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@material-ui/core';
import { getAppFeeCol, getLocalizedAppDetails } from '../../component-helpers/app-details';
import { FilterDropDown } from '../../components/Common/FilterDropDown';
import { Filters, FiltersMap } from '../../components/Common/FilterDropDown/filters';
import { generateRoute } from '../../utils/location';
import { Link as RouteLink, useParams } from 'react-router-dom';
import { AppIcon } from '../../components/Common/AppIcon';
import styles from './styles.module.scss';
import { useTranslation } from 'react-i18next';
import { SetPageTitle } from '../../components/Common/SetPageTitle';
import {
  AppOverFlowMenu,
  AppOverFlowMenuScenario,
  AppOverFlowItem,
  AppOverFlowMenuCallBacks,
} from '../../components/AppDetails/AppOverFlowMenu';
import { useHandlePostRequestResponse } from '../../hooks/useHandlePostRequestResponse';
import { AppPricingAndSubscriptionModal } from '../../components/AppDetails/AppActions/SelectPricing';
import {
  AppActionPostRequestResponseHandler,
  AppSubscriptionSelectRequestHandler, SubmitReviewRequestHandler,
} from '../../components/AppDetails/AppActions/types';
import { truncateString } from '../../utils/helpers';
import { AppReviewModal } from '../../components/AppReviewModal';
import { PageInfo } from '../../components/Header/PageInfo';
import { BackgroundShimmer } from 'components/Shimmer';

enum MyAppsModals {
  RateApp,
  ChangePricing
}

interface AppRowProps {
  app: App;
  handlePostReq: AppActionPostRequestResponseHandler;
}

type MyAppsFilters = Filters.NonCloverApps | Filters.CloverApps;
type MyAppsLimits = Record<MyAppsFilters, number>;

const ENTRIES_PER_PAGE = 15;
const AllowedFilters: MyAppsFilters[] = [Filters.NonCloverApps, Filters.CloverApps];
const initialLimits: MyAppsLimits = {
  [Filters.NonCloverApps]: ENTRIES_PER_PAGE + 1,
  [Filters.CloverApps]: ENTRIES_PER_PAGE + 1,
};

const AppFeeCol: FC<{ app: App, subscription: Maybe<AppSubscriptionCountry> }> = (
  {
    app,
    subscription,
  }): JSX.Element => {
  const { t } = useTranslation();
  const { topText, bottomText } = useMemo(() => getAppFeeCol(app, subscription, t), [app, subscription, t]);
  return (
    <>
      <span style={{ display: 'block' }}>{topText}</span>
      {bottomText && <small>{bottomText}</small>}
    </>
  );
};

const AppRow: FC<AppRowProps> = ({ app, handlePostReq }): Nullable<JSX.Element> => {
  const { i18n: {language} } = useTranslation();
  const { developer, id, currentSubscription } = app;
  const [modal, setModal] = useState<Maybe<MyAppsModals>>();
  const [subscription, setSubscription] = useState<Maybe<AppSubscriptionCountry>>();
  const [appUninstalled, setAppUninstalled] = useState<boolean>(false);
  const { name: devName = '', id: devId } = developer;
  const { name = '', filenameIconSmall } = useMemo(() => getLocalizedAppDetails(app, language), [app, language]);

  useEffect(() => {
    setSubscription(currentSubscription?.subscriptionCountries?.elements[0]);
  }, [currentSubscription]);

  const openModal = useCallback((modalToUse: MyAppsModals) => {
    setModal(modalToUse);
  }, []);

  const closeModal = () => setModal(undefined);

  const appSubscriptionChangeHandler: AppSubscriptionSelectRequestHandler = ({ selectedSubscription, ...rest }) => {
    setSubscription(selectedSubscription);
    handlePostReq(rest);
  };

  const reviewSubmitted: SubmitReviewRequestHandler = (args) => {
    closeModal();
    handlePostReq(args);
  };

  const callBacks = useMemo((): AppOverFlowMenuCallBacks => {
    return {
      [AppOverFlowItem.Uninstall]: (args) => {
        setAppUninstalled(true);
        handlePostReq(args);
      },
      [AppOverFlowItem.RateApp]: () => openModal(MyAppsModals.RateApp),
      [AppOverFlowItem.ChangeSubscription]: () => openModal(MyAppsModals.ChangePricing),
      [AppOverFlowItem.OpenApp]: undefined,
    };
  }, [openModal, handlePostReq]);

  if (appUninstalled) return null;

  return (
    <>
      <TableRow>
        <TableCell align='left' colSpan={2}>
          <div className={styles.appName}>
            <AppIcon src={filenameIconSmall} name={name} size={'sm'} />
            <RouteLink to={generateRoute(AppMarketPageIdentifier.AppDetails, id)} className={styles.appLink}>
              <Typography variant='body1' align='left' noWrap>
                {truncateString(name, 32)}
              </Typography>
            </RouteLink>
          </div>
        </TableCell>
        <TableCell align='left'>
        <div className={styles.devName}>
          <RouteLink to={generateRoute(AppMarketPageIdentifier.DeveloperApps, devId)} className={styles.devLink}>
            <Typography variant='body1' align='left' noWrap>
              {truncateString(devName, 32)}
            </Typography>
          </RouteLink>
          </div>
        </TableCell>
        <TableCell align='left' colSpan={1}>
          <Typography variant='body1' align='left'>
            <AppFeeCol app={app} subscription={subscription} />
          </Typography>
        </TableCell>
        <TableCell align='right'>
          <AppOverFlowMenu
            app={app}
            callbacks={callBacks}
            page={AppOverFlowMenuScenario.MyApps}
          />
        </TableCell>
      </TableRow>
      {
        modal === MyAppsModals.ChangePricing &&
        <AppPricingAndSubscriptionModal
          app={app}
          appSubscriptionSelectHandler={appSubscriptionChangeHandler}
          open={true}
          closePricingModal={closeModal}
        />
      }
      {
        modal === MyAppsModals.RateApp &&
        <AppReviewModal
          app={app}
          closeModal={closeModal}
          open={true}
          reviewSubmittedCallBack={reviewSubmitted}
        />
      }
    </>
  );
};

export const MyApps: FC<AppMarketRouteComponentProps> = ({ history, routes, appMode }): JSX.Element => {
  const { t } = useTranslation();
  const handlePostReq = useHandlePostRequestResponse();
  let { filterKey = Filters.NonCloverApps } = useParams<{ filterKey: Filters }>();
  const [limits, setLimits] = useState<MyAppsLimits>(initialLimits);
  const { sort, filters } = FiltersMap[filterKey];
  const limit = limits[filterKey as MyAppsFilters];
  const { data: installedApps, isLoading, isFetching } = useFetchInstalledAppsQuery({
    limit,
    sorts: sort ? [sort] : undefined,
    filters,
    offset: 0,
  });

  const changeLimit = (apiLimit: number) => {
    const limitToUse = limits[filterKey as MyAppsFilters];
    if (apiLimit !== limitToUse) {
      let newLimits = { ...limits };
      newLimits[filterKey as MyAppsFilters] = apiLimit;
      setLimits(newLimits);
    }
  };

  const filterChangeHandler = (filters: Filters) => {
    history.push(
      generateRoute(AppMarketPageIdentifier.MyApps, filters),
    );
  };

  return (
    <>
      <SetPageTitle title={t('app_nav_tabs.myapps')} />
      <PageInfo hideDescription={true} title={t('app_nav_tabs.myapps')} />
      <FilterDropDown
        onChangeHandlerCallback={filterChangeHandler}
        allowedFilters={AllowedFilters}
        selectedFilter={filterKey}
        label={'drop_down.filter_by'}
      />
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align='left' colSpan={2} scope='col'>{t('my_apps.apps_name')}</TableCell>
              <TableCell align='left' scope='col'>{t('my_apps.developer')}</TableCell>
              <TableCell align='left' scope='col'>{t('my_apps.fees')}</TableCell>
              <TableCell align='right' scope='col'>{t('my_apps.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <ListWithLoadMore<App>
              config={{
                maxEntriesPerPage: ENTRIES_PER_PAGE,
              }}
              isLoading={isLoading}
              isFetching={isFetching}
              items={installedApps}
              setApiLimit={changeLimit}
              renderItem={(app: Maybe<App>, rowIndex: number) => {
                if (!app) return;
                return <AppRow
                  key={app.id}
                  app={app}
                  handlePostReq={handlePostReq}
                />;
              }}
              renderInitialLoader={() => (
                <TableRow>
                  <TableCell align={'center'} colSpan={4}>
                    <BackgroundShimmer smallShimmer={false} />
                  </TableCell>
                </TableRow>
              )
              }
              loaderLength={1}
              renderButtonAsTableRow={true}
            />
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
